import {
  AddAtraafPayload,
  AddZiyaratPayload,
  UpdateAtraafStatusPayload,
  UpdateZiyaratPayload,
  ZiyaratFilters,
} from "types/ziyarat";
import axiosInstance from "./axiosInstance";

type VenueListQuery = {
  onlyActive?: boolean;
};

// Helper to get token safely
export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getVenuesList = async (query: VenueListQuery) => {
  const res = await axiosInstance.get("/Venues", {
    params: query,
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getZiyaratByVenueList = async (id: string) => {
  const res = await axiosInstance.get(`/ziyarat/ziyarat-by-venue/${id}`, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getZiyaratList = async (filters: ZiyaratFilters = {}) => {
  try {
    const response = await axiosInstance.get("/ziyarat/list", {
      params: {
        pageNo: filters.pageNo ?? 0,
        pageSize: filters.pageSize ?? 20,
        searchText: filters.searchTerm || undefined,
        name: filters.placeName || undefined,
        venueId: filters.venue || undefined,
        isActive: filters.status || undefined,
      },
      headers: {
        ...getAuthHeader(),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const AddZiyarat = async (payload: AddZiyaratPayload) => {
  try {
    const response = await axiosInstance.post("/ziyarat", payload, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateZiyarat = async (payload: UpdateZiyaratPayload) => {
  try {
    const response = await axiosInstance.put("/ziyarat", payload, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteZiyaratPlace = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/ziyarat/${id}`, {
    headers: {
      ...getAuthHeader(),
    },
  });
};
// ZIYARAT EXPORT BATCHS API

// ATRAAF APIS START
export interface AtraafRoutePlanDay {
  date: string;
  applicantCount: number;
  isAvailable: boolean;
  status: string;
}

export interface AtraafRoutePlan {
  atraafPublicId: string;
  atraafName: string;
  venuePublicId: string;
  venueName: string;
  capacity: number | null;
  days: AtraafRoutePlanDay[];
}

export interface AtraafRoutePlansFilters {
  VenuePublicId: string;
  AtraafPublicId?: string;
  LoadStatus?: string;
  Search?: string;
  FromDate: string;
  ToDate: string;
}

export const getAtraafRoutePlans = async (params: AtraafRoutePlansFilters) => {
  const response = await axiosInstance.get<{
    success: boolean;
    message: string;
    data: { fromDate: string; toDate: string; routes: AtraafRoutePlan[] };
  }>("/tours/atraaf-route-plans", {
    params,
    headers: getAuthHeader(),
  });
  if (!response.data.success) {
    throw new Error(response.data.message || "Failed to load route plans");
  }
  return response.data.data;
};

export const exportAtraafRoutes = async (params: {
  startDate: string;
  endDate: string;
  venuePublicId: string;
}): Promise<Blob> => {
  const response = await axiosInstance.get<Blob>("/atraaf-route/export", {
    params,
    responseType: "blob",
    headers: getAuthHeader(),
  });
  return response.data;
};

export interface AtraafAssignmentTour {
  tourPublicId: string;
  tourCode: string;
  tourName: string;
  noOfApplicants: number;
}

export interface AtraafAssignmentData {
  assignedTours: AtraafAssignmentTour[];
  notAssignedTours: AtraafAssignmentTour[];
}

export const getAtraafAssignment = async (params: {
  AtraafPublicId: string;
  VenuePublicId: string;
  Date: string;
}): Promise<AtraafAssignmentData> => {
  const response = await axiosInstance.get<{
    success: boolean;
    message: string;
    data: AtraafAssignmentData;
    errors?: string[];
  }>("/tours/atraaf-assignment", { params, headers: getAuthHeader() });
  if (!response.data.success) {
    throw new Error(response.data.message || "Failed to load tour assignments");
  }
  return response.data.data;
};

export const assignTourAtraafs = async (tourPublicId: string, payload: {
  venuePublicId: string;
  venueDate: string;
  atraafPublicIds: string[];
}) => {
  const response = await axiosInstance.post<{ success?: boolean; message?: string }>(
    `/tours/${encodeURIComponent(tourPublicId)}/atraafs-assign`,
    payload,
    { headers: getAuthHeader() },
  );
  if (response.data?.success === false) {
    throw new Error(response.data.message || "Failed to assign tour");
  }
  return response.data;
};

export interface AtraafRouteOption {
  atraafId: number;
  publicId: string;
  atraafName: string;
  venueId: number;
  capacity: number | null;
}

export const getAtraafRouteOptions = async (venueId: number): Promise<AtraafRouteOption[]> => {
  const response = await axiosInstance.get("/atraaf", {
    params: { venueId, isActive: true },
    headers: getAuthHeader(),
  });
  return response.data;
};

export const getAtraafList = async (filters: ZiyaratFilters = {}) => {
  try {
    const response = await axiosInstance.get("/atraaf/list", {
      params: {
        pageNo: filters.pageNo ?? 0,
        pageSize: filters.pageSize ?? 20,
        searchText: filters.searchTerm || undefined,
        atraafName: filters.routeName || undefined,
        venueId: filters.venue || undefined,
        transportRequired: filters.transport || undefined,
        optionalRoute: filters.type || undefined,
        lawazimRequired: filters.lawazimRequired || undefined,
        isActive: filters.status || undefined,
      },
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTravelTypes = async () => {
  try {
    const response = await axiosInstance.get(`/travel-types/list`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const AddAtraaf = async (payload: AddAtraafPayload) => {
  try {
    const response = await axiosInstance.post("/atraaf/create", payload, {
      headers: {
        ...getAuthHeader(),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const EditAtraaf = async (payload: AddAtraafPayload) => {
  try {
    const response = await axiosInstance.put("/atraaf/update", payload, {
      headers: {
        ...getAuthHeader(),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UpdateAtraafStatus = async (payload: UpdateAtraafStatusPayload) => {
  try {
    const response = await axiosInstance.put(
      `/atraaf/status/${payload.publicId}`,
      {
        isActive: payload.isActive, //body payload
      },
      {
        headers: {
          ...getAuthHeader(),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAtraafByPublicId = async (publicId: string) => {
  try {
    const response = await axiosInstance.get(`/atraaf/${publicId}`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAtraaf = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/atraaf/${id}`, {
    headers: {
      ...getAuthHeader(),
    },
  });
};
// ATRAAF APIS END
